import React, { useEffect, useRef, useState } from "react"
import { Button, Col, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import Switch from "components/Shared/Switch"
import { DatePicker } from "rsuite"
import Select from "components/Shared/TranslatedSelect"
import { getAllCustomersList } from "store/actions/customersAction"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { sendNotificationSchema } from "components/Common/validation"
import { sendNotification } from "store/actions/pushNotificationAction"
import { showAlert } from "store/reducers/alertReducer"
import { disablePastDates } from "components/Common/constants"
import TextError from "components/Shared/TextError"
import useCrossModuleFilterPermissions from "hooks/useCrossModuleFilterPermissions"

const CUSTOMER_MENU_MAX_HEIGHT = 240
const CUSTOMER_MENU_MIN_HEIGHT = 80
const CUSTOMER_MENU_VIEWPORT_GAP = 8

const customerSelectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  menuPlacement: "auto",
  menuShouldScrollIntoView: false,
}

const getCustomerSelectStyles = maxMenuHeight => ({
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({
    ...base,
    zIndex: 9999,
    maxHeight: maxMenuHeight,
    overflow: "hidden",
  }),
  menuList: base => ({
    ...base,
    maxHeight: maxMenuHeight,
    overflowY: "auto",
  }),
})

const getVisibleMenuMaxHeight = anchorEl => {
  if (!anchorEl || typeof window === "undefined") {
    return CUSTOMER_MENU_MAX_HEIGHT
  }

  const rect = anchorEl.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom - CUSTOMER_MENU_VIEWPORT_GAP
  const spaceAbove = rect.top - CUSTOMER_MENU_VIEWPORT_GAP
  const available = Math.max(spaceBelow, spaceAbove)

  return Math.max(
    CUSTOMER_MENU_MIN_HEIGHT,
    Math.min(CUSTOMER_MENU_MAX_HEIGHT, available)
  )
}

const initialValues = {
  type: "all",
  titleEn: "",
  titleAr: "",
  bodyEn: "",
  bodyAr: "",
  isScheduled: false,
  scheduledTime: "",
  customers: [],
}

const SendNotification = ({ t }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { customersDropdownList } = useSelector(state => state.Customers)
  const { isLoading } = useSelector(state => state.PushNotifications)
  const { canFilterByCustomers } = useCrossModuleFilterPermissions()

  const [customerOptions, setCustomerOptions] = useState([])
  const [customerMenuMaxHeight, setCustomerMenuMaxHeight] = useState(
    CUSTOMER_MENU_MAX_HEIGHT
  )
  const customerSelectWrapRef = useRef(null)

  useEffect(() => {
    if (customersDropdownList.length > 0) {
      const updatedCustomerOption = customersDropdownList?.map(item => ({
        label: `${item.firstName} ${item.lastName}, ${
          item?.phoneNumber ? item?.phoneNumber : ""
        }`,
        value: item._id,
      }))
      setCustomerOptions(updatedCustomerOption)
      //   console.log(customersDropdownList)
    }
  }, [customersDropdownList])

  const fetchCustomersList = keyword => {
    if (!canFilterByCustomers) return
    const payload = {
      keyword,
    }
    dispatch(getAllCustomersList(payload))
  }

  const handleOnSubmit = (values, { resetForm }) => {
    const {
      type,
      titleEn,
      titleAr,
      bodyEn,
      bodyAr,
      isScheduled,
      scheduledTime,
      customers,
    } = values

    const updatedCustomers = customers ? customers?.map(item => item.value) : []

    const payload = {
      type: type, //all or specific-customers
      title: {
        en: titleEn,
        ar: titleAr,
      },
      body: {
        en: bodyEn,
        ar: bodyAr,
      },
      sound: "default",
      isScheduled: isScheduled,
      scheduledTime: scheduledTime, //pass date in case of isScheduled = true
      customers: updatedCustomers,
      data: {},
    }

    dispatch(sendNotification(payload))
      .then(action => {
        const { statusCode, status, msg, message } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          resetForm()
        } else {
          dispatch(showAlert({ text: msg || message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <Row>
      <Col sm="12">
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={sendNotificationSchema()}
          validateOnBlur
          validateOnChange={false}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="titleEn" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Title(English)")}
                    </label>
                    <Field
                      name="titleEn"
                      type="text"
                      id="titleEn"
                      maxLength={255}
                      placeholder={t("Title(English)")}
                      className={`form-control ${
                        touched.titleEn && errors.titleEn ? "is-invalid" : ""
                      }`}
                    />
                    <TextError name="titleEn" />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="titleAr" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Title(عربي)")}
                    </label>
                    <Field
                      name="titleAr"
                      type="text"
                      id="titleAr"
                      maxLength={255}
                      style={{ direction: "rtl" }}
                      placeholder={t("Title(عربي)")}
                      className={`form-control ${
                        touched.titleAr && errors.titleAr ? "is-invalid" : ""
                      }`}
                    />
                    <TextError name="titleAr" />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="bodyEn" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Message(English)")}
                    </label>
                    <Field
                      as="textarea"
                      id="bodyEn"
                      name="bodyEn"
                      rows={3}
                      placeholder={t("Message(English)")}
                      className={`form-control ${
                        touched.bodyEn && errors.bodyEn ? "is-invalid" : ""
                      }`}
                    />
                    <TextError name="bodyEn" />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="bodyAr" className="col col-form-label">
                      <span className="text-danger">* </span>
                      {t("Message(عربي)")}
                    </label>
                    <Field
                      as="textarea"
                      id="bodyAr"
                      name="bodyAr"
                      rows={3}
                      placeholder={t("Message(عربي)")}
                      style={{ direction: "rtl" }}
                      className={`form-control ${
                        touched.bodyAr && errors.bodyAr ? "is-invalid" : ""
                      }`}
                    />
                    <TextError name="bodyAr" />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mt-3">
                    <label htmlFor="name">{t("Select Type")}</label>
                    <div className="d-flex ">
                      <div className="form-check mx-2">
                        <Field
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="all"
                          value="all"
                          onClick={() => {
                            setFieldValue("type", "all")
                          }}
                        />
                        <label className="form-check-label" htmlFor="all">
                          {t("All")}
                        </label>
                      </div>
                      <div className="form-check mx-2">
                        <Field
                          className="form-check-input"
                          type="radio"
                          name="type"
                          id="specific-customers"
                          value="specific-customers"
                          onClick={() => {
                            setFieldValue("type", "specific-customers")
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="specific-customers"
                        >
                          {t("Specific Customers")}
                        </label>
                      </div>
                    </div>
                  </div>
                </Col>
                {values.type === "specific-customers" && (
                  <Col md={6}>
                    <>
                      <label htmlFor="">
                        <span className="text-danger">*</span>{" "}
                        {t("Please Type To Search Customer")}
                      </label>
                      <div ref={customerSelectWrapRef}>
                        <Select
                          value={values.customers}
                          isMulti={true}
                          onChange={selectedValue => {
                            setFieldValue("customers", selectedValue)
                          }}
                          options={customerOptions?.map(item => ({
                            label: item.label,
                            value: item.value,
                          }))}
                          onInputChange={value => fetchCustomersList(value)}
                          placeholder={t("Please Type To Search Customer")}
                          noOptionsMessage={() => t("No options")}
                          className={`select2-selection ${
                            touched.customers && errors.customers
                              ? "danger_color_border"
                              : ""
                          } `}
                          onFocus={() => {
                            setCustomerMenuMaxHeight(
                              getVisibleMenuMaxHeight(
                                customerSelectWrapRef.current
                              )
                            )
                          }}
                          onMenuOpen={() => {
                            setCustomerMenuMaxHeight(
                              getVisibleMenuMaxHeight(
                                customerSelectWrapRef.current
                              )
                            )
                          }}
                          maxMenuHeight={customerMenuMaxHeight}
                          minMenuHeight={CUSTOMER_MENU_MIN_HEIGHT}
                          styles={getCustomerSelectStyles(customerMenuMaxHeight)}
                          {...customerSelectMenuProps}
                        />
                      </div>
                      <TextError name="customers" />
                    </>
                  </Col>
                )}
                <Col md={6}>
                  <div className="form_field">
                    <label htmlFor="">{t("Scheduled Notification")}</label>
                    <Switch
                      isChecked={values.isScheduled}
                      onClickHandler={() => {
                        setFieldValue("isScheduled", !values.isScheduled)
                      }}
                    />
                  </div>
                </Col>
                {values.isScheduled && (
                  <Col md={6}>
                    <div className="form_field full_rs_picker">
                      <label htmlFor="endTime">
                        <span className="text-danger">*</span>{" "}
                        {t("Schedule Date & Time")}
                      </label>
                      <DatePicker
                        showTime={{ format: "HH:mm" }} // Show time picker
                        format="yyyy-MM-dd HH:mm" // Custom format for displaying date and time
                        onChange={value => {
                          setFieldValue("scheduledTime", value)
                        }}
                        placement="autoVerticalStart"
                        value={values.scheduledTime}
                        placeholder=" 2024-07-16 14:30"
                        disabledDate={disablePastDates}
                      />

                      <TextError
                        name="scheduledTime"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </Col>
                )}
                <Col md={12}>
                  <div className="d-flex align-items-center justify-content-end mt-5">
                    {/* <Button
                      type="button"
                      color="secondary"
                      className=" mx-2"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button> */}
                    <Button
                      color="success"
                      type="submit"
                      style={{ width: "fit-content" }}
                      disabled={isLoading}
                    >
                      {values.isScheduled ? t("Save") : t("Send")}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  )
}

export default SendNotification
